import { Button, Combobox, InputBase, Loader, Stack, useCombobox } from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { Right } from 'components/Right';
import { tKeys, useTranslate } from 'locales/index';
import { useRef, useState } from 'react';
import { useCreateReason, useLoadReasons } from '~/modules/Settings/Variables/Reasons/api';

export type DisputeReasonFormValues = { reason_id: string };

type DisputeReasonFormProps = {
  onSubmit: (values: DisputeReasonFormValues) => unknown;
};

export const DisputeReasonForm = ({ onSubmit }: DisputeReasonFormProps) => {
  const t = useTranslate();

  const { reasons } = useLoadReasons({ reasonType: 'dispute' });
  const { createReason, isCreateReasonLoading } = useCreateReason();

  const isCreatingReasonRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState('');

  const form = useForm<DisputeReasonFormValues>({
    initialValues: { reason_id: '' },
    validate: {
      reason_id: value => (value ? null : t(tKeys.ERROR.FIELD_REQUIRED)),
    },
  });

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const reasonsList = reasons.data;

  const filteredReasons = reasonsList.filter(reason =>
    reason.name.toLowerCase().includes(search.toLowerCase().trim()),
  );

  const hasExactMatch = reasonsList.some(
    reason => reason.name.toLowerCase() === search.toLowerCase().trim(),
  );
  const canCreate = search.trim().length > 0 && !hasExactMatch;

  const handleOptionSubmit = (value: string) => {
    if (value === '$create') {
      if (isCreatingReasonRef.current) return;
      isCreatingReasonRef.current = true;
      createReason(
        { name: search.trim(), reasonType: 'dispute' },
        {
          onError: () => {
            isCreatingReasonRef.current = false;
          },
          onSuccess: newReason => {
            if (!isCreatingReasonRef.current) return;
            form.setFieldValue('reason_id', String(newReason.id));
            combobox.closeDropdown();
          },
        },
      );
      return;
    }

    isCreatingReasonRef.current = false;

    form.setFieldValue('reason_id', value);
    const selected = reasonsList.find(reason => String(reason.id) === value);
    setSearch(selected?.name ?? '');
    combobox.closeDropdown();
  };

  return (
    <form
      onSubmit={form.onSubmit(async values => {
        setIsSubmitting(true);
        try {
          await onSubmit(values);
        } finally {
          modals.closeAll();
        }
      })}
    >
      <Stack gap="md">
        <Combobox store={combobox} onOptionSubmit={handleOptionSubmit}>
          <Combobox.Target>
            <InputBase
              label={t(tKeys.FORM.REASON)}
              description={t(tKeys.ADD_REASON_HINT)}
              placeholder={t(tKeys.FORM.REASON)}
              rightSection={isCreateReasonLoading ? <Loader size="xs" /> : <Combobox.Chevron />}
              rightSectionPointerEvents="none"
              value={search}
              error={form.errors.reason_id}
              onChange={event => {
                combobox.openDropdown();
                combobox.updateSelectedOptionIndex();
                setSearch(event.currentTarget.value);
                isCreatingReasonRef.current = false;
                form.setFieldValue('reason_id', '');
              }}
              onClick={() => combobox.openDropdown()}
              onFocus={() => combobox.openDropdown()}
              onBlur={() => {
                combobox.closeDropdown();
                if (!form.values.reason_id) setSearch('');
              }}
            />
          </Combobox.Target>

          <Combobox.Dropdown>
            <Combobox.Options>
              {filteredReasons.map(reason => (
                <Combobox.Option key={reason.id} value={String(reason.id)}>
                  {reason.name}
                </Combobox.Option>
              ))}

              {canCreate && (
                <Combobox.Option value="$create" c="dimmed">
                  + {search.trim()}
                </Combobox.Option>
              )}

              {filteredReasons.length === 0 && !canCreate && (
                <Combobox.Empty>{t(tKeys.NO_RESULT)}</Combobox.Empty>
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        <Right>
          <Button type="submit" loading={isSubmitting} disabled={!form.values.reason_id}>
            {t(tKeys.CONFIRM)}
          </Button>
        </Right>
      </Stack>
    </form>
  );
};
